import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { GridTileImage } from '../components/grid/tile';
import Footer from '../components/layout/footer';
import { Gallery } from '../components/product/gallery';
import { ProductProvider } from '../components/product/product-context';
import { ProductDescription } from '../components/product/product-description';
import { getProduct, getProductRecommendations } from '../lib/burokshop';
import type { Product, Image } from '../lib/burokshop/types';
import { useMarketplace } from '../context/MarketplaceContext';

export default function ProductPage() {
  const { handle } = useParams<{ handle: string }>();
  const { marketplaceType } = useMarketplace();
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProduct() {
      if (!handle) return;
      
      setLoading(true);
      const productData = await getProduct(handle, marketplaceType);
      if (productData) {
        setProduct(productData);
        const related = await getProductRecommendations(productData.id, marketplaceType);
        setRelatedProducts(related);
      }
      setLoading(false);
    }
    loadProduct();
  }, [handle, marketplaceType]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Un instant...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Produit non trouvé</div>
      </div>
    );
  }

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description: product.description,
    image: product.featuredImage.url,
    offers: {
      '@type': 'AggregateOffer',
      availability: product.availableForSale
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      priceCurrency: product.priceRange.minVariantPrice.currencyCode,
      highPrice: product.priceRange.maxVariantPrice.amount,
      lowPrice: product.priceRange.minVariantPrice.amount
    }
  };

  return (
    <ProductProvider>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(productJsonLd)
        }}
      />
      <div className="mx-auto max-w-(--breakpoint-2xl) px-4">
        <div className="flex flex-col rounded-lg border border-neutral-200 bg-white p-8 md:p-12 lg:flex-row lg:gap-8 dark:border-neutral-800 dark:bg-black">
          <div className="h-full w-full basis-full lg:basis-4/6">
            <Gallery
              images={product.images.slice(0, 5).map((image: Image) => ({
                src: image.url,
                altText: image.altText
              }))}
            />
          </div>

          <div className="basis-full lg:basis-2/6">
            <ProductDescription product={product} />
          </div>
        </div>
        {relatedProducts.length > 0 && (
          <div className="py-8">
            <h2 className="mb-4 text-2xl font-bold">Produits similaires</h2>
            <ul className="flex w-full gap-4 overflow-x-auto pt-1">
              {relatedProducts.map((relatedProduct) => (
                <li
                  key={relatedProduct.handle}
                  className="aspect-square w-full flex-none min-[475px]:w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5"
                >
                  <Link
                    className="relative h-full w-full"
                    to={`/product/${relatedProduct.handle}`}
                  >
                    <GridTileImage
                      alt={relatedProduct.title}
                      label={{
                        title: relatedProduct.title,
                        amount: relatedProduct.priceRange.maxVariantPrice.amount,
                        currencyCode: relatedProduct.priceRange.maxVariantPrice.currencyCode
                      }}
                      src={relatedProduct.featuredImage?.url}
                      fill
                      sizes="(min-width: 1024px) 20vw, (min-width: 768px) 25vw, (min-width: 640px) 33vw, (min-width: 475px) 50vw, 100vw"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <Footer />
    </ProductProvider>
  );
}

